import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Ear, Headphones, ImagePlus, Mic, Send, Square, Trash2, X } from "lucide-react";
import Avatar from "@/components/Avatar";
import ChatMessage from "@/components/studio/ChatMessage";
import useRecorder from "@/hooks/useRecorder";
import useSpeaker from "@/hooks/useSpeaker";
import useHandsFree from "@/hooks/useHandsFree";
import useWakeWord, { chime } from "@/hooks/useWakeWord";
import { fileToBase64 } from "@/lib/audio";
import { getSessionId, http, speak, streamChat, transcribe } from "@/lib/api";

export default function ChatPanel() {
  const sid = getSessionId();
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [photo, setPhoto] = useState(null);
  const [state, setState] = useState("idle");
  const [params, setParams] = useSearchParams();
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const busy = useRef(false);
  const started = useRef(false);
  const rec = useRecorder();
  const speaker = useSpeaker();
  const hf = useHandsFree((blob) => handleAudio(blob), () => onIdle());
  const hfOn = useRef(false);
  hfOn.current = hf.active;
  const [wakeOn, setWakeOn] = useState(localStorage.getItem("semia_wake") === "on");
  const wake = useWakeWord(() => onWake(), () => { setWakeOn(false); toast.error("Autorisez le micro pour le mot de réveil"); });

  useEffect(() => {
    if (wakeOn && wake.supported && !hf.active && !rec.recording) wake.start();
    else wake.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wakeOn, hf.active, rec.recording]);

  useEffect(() => () => wake.stop(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const onWake = async () => {
    chime();
    toast.success("Oui ? Je vous écoute !");
    try {
      speaker.stop();
      await hf.start();
      setState("listening");
    } catch {
      toast.error("Micro indisponible");
    }
  };

  const onIdle = () => {
    if (localStorage.getItem("semia_wake") !== "on" || busy.current) return;
    hf.stop();
    setState("idle");
    toast("Je retourne en veille. Dites « Hey Sem.IA » pour me réveiller.");
  };

  const toggleWake = () => {
    if (!wake.supported) return toast.error("Mot de réveil non supporté par ce navigateur (utilisez Chrome, Edge ou Safari)");
    const on = !wakeOn;
    setWakeOn(on);
    localStorage.setItem("semia_wake", on ? "on" : "off");
    toast(on ? "Dites « Hey Sem.IA » pour commencer à parler" : "Mot de réveil désactivé");
  };

  useEffect(() => {
    http.get(`/chat/${sid}/messages`).then((r) => setMsgs(r.data)).catch(() => {});
    const q = params.get("q");
    if (q && !started.current) {
      started.current = true;
      setParams({});
      send(q);
    }
    return () => hf.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), [msgs]);

  const settle = () => {
    busy.current = false;
    if (hfOn.current) { hf.resume(); setState("listening"); } else setState("idle");
  };

  const play = async (text) => {
    try {
      hf.pause();
      setState("speaking");
      await speaker.play(await speak(text, localStorage.getItem("semia_voice_v2") || "coral"));
    } catch {
      toast.error("Lecture vocale impossible");
    }
    settle();
  };

  const send = async (text, img = photo) => {
    if ((!text.trim() && !img) || busy.current) return;
    busy.current = true;
    hf.pause();
    const content = text.trim() || "Décris et commente cette photo.";
    setInput("");
    setPhoto(null);
    setMsgs((m) => [...m, { id: `u${Date.now()}`, role: "user", content, preview: img?.url }, { id: "pending", role: "assistant", content: "" }]);
    setState("thinking");
    try {
      const full = await streamChat(sid, content, (t) => setMsgs((m) => m.map((x) => (x.id === "pending" ? { ...x, content: t } : x))), img);
      setMsgs((m) => m.map((x) => (x.id === "pending" ? { ...x, id: `a${Date.now()}` } : x)));
      if (hfOn.current || localStorage.getItem("lea_autospeak") !== "off") return play(full);
    } catch (e) {
      setMsgs((m) => m.filter((x) => x.id !== "pending"));
      toast.error(e.message);
    }
    settle();
  };

  async function handleAudio(blob) {
    if (busy.current) return;
    hf.pause();
    setState("thinking");
    try {
      const text = await transcribe(blob);
      if (text.trim()) return send(text);
      toast("Je n'ai rien entendu, réessayez.");
    } catch {
      toast.error("Transcription impossible");
    }
    settle();
  }

  const toggleMic = async () => {
    if (rec.recording) return handleAudio(await rec.stop());
    try {
      speaker.stop();
      await rec.start();
      setState("listening");
    } catch {
      toast.error("Autorisez l'accès au micro pour parler à Sem.IA");
    }
  };

  const toggleHandsFree = async () => {
    if (hf.active) { hf.stop(); setState("idle"); return toast("Mode mains libres désactivé"); }
    try {
      speaker.stop();
      await hf.start();
      setState("listening");
      toast.success("Mains libres activé : parlez naturellement, Sem.IA vous écoute.");
    } catch {
      toast.error("Autorisez l'accès au micro pour le mode mains libres");
    }
  };

  const pickPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) return toast.error("Photo trop lourde (8 Mo max)");
    const url = await fileToBase64(f);
    setPhoto({ url, base64: url.split(",")[1], mime: f.type || "image/jpeg" });
  };

  const clear = async () => {
    await http.delete(`/chat/${sid}`);
    setMsgs([]);
    toast.success("Conversation effacée");
  };

  return (
    <div className="card overflow-hidden grid grid-rows-[auto_1fr] lg:grid-rows-1 lg:grid-cols-[260px_1fr] h-[calc(100vh-180px)] min-h-[600px]">
      <div className="flex lg:flex-col items-center justify-center gap-4 p-4 lg:p-6 bg-[var(--surface-subtle)] border-b lg:border-b-0 lg:border-r border-[var(--border)]">
        <Avatar state={hf.hearing ? "listening" : state} analyser={speaker.analyser} className="w-[110px] lg:w-[200px] shrink-0" />
        <div className="flex flex-col gap-2 w-full max-w-[200px]">
          <p className="font-serif text-2xl hidden lg:block text-center">Sem.IA</p>
          <button data-testid="handsfree-toggle-button" onClick={toggleHandsFree}
            className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm transition-colors ${hf.active ? "bg-[var(--accent)] text-white" : "border border-[var(--border)] hover:bg-[var(--text)] hover:text-[var(--bg)]"}`}>
            <Headphones size={16} /> {hf.active ? "Mains libres : ON" : "Mains libres"}
          </button>
          <button data-testid="wakeword-toggle-button" onClick={toggleWake}
            className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm transition-colors ${wakeOn ? "bg-[var(--accent-2)] text-white" : "border border-[var(--border)] hover:bg-[var(--text)] hover:text-[var(--bg)]"}`}>
            <Ear size={16} /> {wakeOn ? "« Hey Sem.IA » : ON" : "Mot de réveil"}
          </button>
          {wakeOn && !hf.active && <p data-testid="wakeword-status" className="text-xs text-center text-[var(--text-muted)]">{wake.listening ? "Dites « Hey Sem.IA »" : "Activation…"}</p>}
          {hf.active && <p data-testid="handsfree-status" className="text-xs text-center text-[var(--text-muted)]">{hf.hearing ? "Je vous entends…" : "Parlez quand vous voulez"}</p>}
          <button data-testid="chat-clear-button" onClick={clear} className="flex items-center justify-center gap-2 text-xs text-[var(--text-muted)] hover:text-[var(--accent)]"><Trash2 size={14} /> Effacer</button>
        </div>
      </div>
      <div className="flex flex-col min-h-0">
        <div data-testid="chat-messages-list" className="flex-1 overflow-y-auto p-6 space-y-4">
          {msgs.length === 0 && <p className="text-center text-[var(--text-secondary)] mt-16 max-w-sm mx-auto">Bonjour ! Parlez-moi au micro, activez le mode mains libres, envoyez-moi une photo ou écrivez-moi.</p>}
          {msgs.map((m) => <ChatMessage key={m.id} m={m} onPlay={play} />)}
          <div ref={endRef} />
        </div>
        {photo && (
          <div className="px-4 pt-3 flex items-center gap-3">
            <img data-testid="chat-photo-preview" src={photo.url} alt="aperçu" className="h-16 w-16 object-cover rounded-xl" />
            <button data-testid="chat-photo-remove" onClick={() => setPhoto(null)} className="icon-btn !w-8 !h-8"><X size={14} /></button>
          </div>
        )}
        <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex items-end gap-2 p-4 border-t border-[var(--border)]">
          <button type="button" data-testid="voice-mic-toggle-button" onClick={toggleMic} disabled={hf.active}
            className={`w-12 h-12 shrink-0 rounded-full flex items-center justify-center transition-colors disabled:opacity-40 ${rec.recording ? "bg-[var(--accent)] text-white animate-pulse" : "bg-[var(--accent-soft)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-white"}`}>
            {rec.recording ? <Square size={18} /> : <Mic size={20} />}
          </button>
          <button type="button" data-testid="chat-photo-button" onClick={() => fileRef.current.click()} className="icon-btn !w-12 !h-12 shrink-0" title="Envoyer une photo"><ImagePlus size={18} /></button>
          <input ref={fileRef} data-testid="chat-photo-input" type="file" accept="image/*" className="hidden" onChange={pickPhoto} />
          <textarea data-testid="chat-input-textarea" rows={1} value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
            placeholder={photo ? "Une question sur cette photo ?" : "Écrivez à Sem.IA…"} className="field resize-none flex-1 max-h-40 min-w-0" />
          <button data-testid="chat-send-button" disabled={(!input.trim() && !photo) || state === "thinking"} className="btn-primary h-12 w-12 !p-0 shrink-0"><Send size={18} /></button>
        </form>
      </div>
    </div>
  );
}
